import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type ThemeMode = 'light' | 'dark';

export interface ThemeContextValue {
  theme: ThemeMode;
  toggle: () => void;
  set: (next: ThemeMode) => void;
}

const STORAGE_KEY = 'kai-template:theme';

// eslint-disable-next-line react-refresh/only-export-components
export const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const resolveInitialTheme = (): ThemeMode => {
  if (typeof window === 'undefined') return 'light';

  // The inline bootstrap script in index.html already resolved the theme and
  // stamped it on <html> before first paint. Trust it, so React state matches
  // what is already on screen and the mount effect is a no-op rather than a
  // second, visible theme switch.
  const painted = document.documentElement.getAttribute('data-theme');
  if (painted === 'light' || painted === 'dark') return painted;

  const stored = window.localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
  if (stored === 'light' || stored === 'dark') return stored;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<ThemeMode>(resolveInitialTheme);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    window.localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  const set = useCallback((next: ThemeMode) => setTheme(next), []);
  const toggle = useCallback(
    () => setTheme((prev) => (prev === 'light' ? 'dark' : 'light')),
    [],
  );

  const value = useMemo(() => ({ theme, set, toggle }), [theme, set, toggle]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
